/**
 * alertEvaluator.ts
 *
 * Pure function — no side effects.
 * Takes a test definition and a test result and returns the list of
 * AlertThreshold entries that were triggered.
 */
import { TestDefinition, TestResult, AlertThreshold } from '../types';

export interface TriggeredAlert {
  threshold: AlertThreshold;
  /** The score value that triggered the alert (totalScore or item value). */
  triggeredScore: number;
}

/**
 * Evaluate all alertThresholds for a test against a result.
 * Returns only the thresholds that fired.
 */
export function evaluateAlerts(
  test: TestDefinition,
  result: TestResult,
): TriggeredAlert[] {
  if (!test.alertThresholds?.length) return [];

  const triggered: TriggeredAlert[] = [];

  for (const threshold of test.alertThresholds) {
    // Determine which value to test
    let value: number;
    if (threshold.questionId !== undefined) {
      // Skip if the question is not present in this result (avoids false positives)
      if (!(threshold.questionId in result.answers)) continue;
      value = result.answers[threshold.questionId];
    } else {
      value = result.totalScore;
    }

    const fires =
      threshold.condition === 'gte' ? value >= threshold.score :
      threshold.condition === 'lte' ? value <= threshold.score :
      /* eq */                         value === threshold.score;

    if (fires) {
      triggered.push({ threshold, triggeredScore: value });
    }
  }

  return triggered;
}
